import type { VisualTemplate } from '@/lib/visualTemplates';
import type { Card, CardLink, CardLinkSection, Json } from '@/types/database';
import type { SectionType } from '@/lib/sectionTypes';

/**
 * Builds a complete, in-memory { card, sections, links } shaped like a real
 * Supabase row but with example content for each section the template seeds.
 * Used by the template Preview modal so the user sees the template rendered
 * exactly the way their final public card would look — same layout, same
 * theme, same section variety.
 */
export function templatePreviewData(tpl: VisualTemplate): {
  card: Card;
  sections: CardLinkSection[];
  links: CardLink[];
} {
  const cardId = `preview-${tpl.id}`;
  const now = new Date().toISOString();

  const card: Card = {
    id: cardId,
    user_id: '__preview__',
    slug: `${tpl.id}-preview`,
    display_name: 'Alex Carter',
    title: defaultTitleFor(tpl.id),
    company: defaultCompanyFor(tpl.id),
    bio: tpl.suggestedBio ?? null,
    photo_url: null,
    cover_image_url: null,
    company_logo_url: null,
    theme_preset: tpl.theme_preset,
    custom_accent: tpl.theme_preset === 'custom' ? (tpl.custom_accent ?? null) : null,
    custom_bg: tpl.theme_preset === 'custom' ? (tpl.custom_bg ?? null) : null,
    custom_card_bg: tpl.theme_preset === 'custom' ? (tpl.custom_card_bg ?? null) : null,
    custom_text: tpl.theme_preset === 'custom' ? (tpl.custom_text ?? null) : null,
    exchange_phone: null,
    webhook_url: null,
    logo_scale: 1,
    is_default: false,
    sort_order: 0,
    view_count: 0,
    primary_cta_label: null,
    primary_cta_url: null,
    secondary_cta_label: null,
    secondary_cta_url: null,
    payment_config: { currency: 'USD', shop_enabled: true, venmo: { enabled: true, handle: 'alex-carter' } },
    layout_order: null,
    created_at: now,
    updated_at: now,
  };

  const sections: CardLinkSection[] = tpl.sections.map((s, i) => ({
    id: `${cardId}-s${i}`,
    card_id: cardId,
    name: s.name,
    type: s.type,
    sort_order: i,
    created_at: now,
  }));

  const links: CardLink[] = [];
  sections.forEach((section, sIndex) => {
    const items = exampleItemsFor(section.type as SectionType);
    items.forEach((item, iIndex) => {
      links.push({
        id: `${cardId}-l${sIndex}-${iIndex}`,
        card_id: cardId,
        section_id: section.id,
        type: item.type,
        label: item.label,
        value: item.value,
        description: item.description ?? null,
        metadata: item.metadata ?? null,
        sort_order: iIndex,
        created_at: now,
      });
    });
  });

  return { card, sections, links };
}

function defaultTitleFor(templateId: string): string {
  switch (templateId) {
    case 'founder': return 'Founder, Energy Domain';
    case 'realtor': return 'Realtor, City Lights Realty';
    case 'podcaster': return 'Host, The Quiet Hour';
    case 'consultant': return 'Strategic Advisor';
    case 'creative': return 'Photographer & Director';
    case 'speaker': return 'Keynote Speaker';
    case 'maker': return 'Ceramicist & Printmaker';
    default: return 'Your title';
  }
}

function defaultCompanyFor(templateId: string): string {
  switch (templateId) {
    case 'founder': return 'Energy Domain';
    case 'realtor': return 'City Lights Realty';
    case 'podcaster': return 'Studio Notes';
    case 'consultant': return 'Carter & Co';
    case 'creative': return 'Carter Studio';
    case 'speaker': return 'Independent';
    case 'maker': return 'Carter Studio';
    default: return 'Your company';
  }
}

/**
 * Per section type, generate 1-3 example items that show off what the section
 * actually looks like populated. Values intentionally point at example.com
 * so nothing is clickable in a meaningful way during preview.
 */
function exampleItemsFor(type: SectionType): Array<{
  type: string;
  label: string;
  value: string;
  description?: string;
  metadata?: Json;
}> {
  switch (type) {
    case 'products':
      return [
        {
          type: 'product',
          label: 'Signed print — "Harbor"',
          value: 'https://images.unsplash.com/photo-1604079628040-94301bb21b91?w=800',
          description: '12×16 giclée on cotton rag. Edition of 50.',
          metadata: {
            price_cents: 8500,
            currency: 'USD',
            available: true,
            published: true,
            badges: ['Limited'],
            bullets: ['Archival inks on cotton rag', 'Signed and numbered', 'Ships flat in 3–5 days'],
            options: [{ id: 'size', name: 'Size', type: 'buttons', values: ['12×16', '18×24'] }],
          },
        },
        {
          type: 'product',
          label: 'Studio session — 1 hr',
          value: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800',
          description: 'Portrait session in the studio. Includes 10 edited frames.',
          metadata: { price_cents: 25000, currency: 'USD', available: true, published: true, badges: [], bullets: [], options: [] },
        },
      ];
    case 'contact':
      return [
        { type: 'phone', label: 'Cell', value: '+1 555 010 1010' },
        { type: 'email', label: 'Email', value: 'hello@example.com' },
        { type: 'website', label: 'Website', value: 'https://example.com' },
      ];
    case 'social':
      return [
        { type: 'linkedin', label: 'LinkedIn', value: 'https://linkedin.com/in/example' },
        { type: 'twitter', label: 'Twitter', value: 'https://x.com/example' },
        { type: 'instagram', label: 'Instagram', value: 'https://instagram.com/example' },
      ];
    case 'videos':
      return [
        {
          type: 'youtube',
          label: 'A short reflection on craft',
          value: 'https://youtu.be/dQw4w9WgXcQ',
          description: '8 min. A conversation about the cost of attention.',
        },
        {
          type: 'youtube',
          label: 'On building quietly',
          value: 'https://youtu.be/dQw4w9WgXcQ',
          description: '12 min. Why restraint compounds over a decade.',
        },
      ];
    case 'gallery':
      return [
        {
          type: 'other',
          label: 'Recent work',
          value: 'https://images.unsplash.com/photo-1604079628040-94301bb21b91?w=800',
          description: 'Studio, late afternoon.',
        },
        {
          type: 'other',
          label: 'Recent work',
          value: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800',
          description: 'On location, Houston.',
        },
        {
          type: 'other',
          label: 'Recent work',
          value: 'https://images.unsplash.com/photo-1531913764164-f85c52e6e654?w=800',
          description: 'Editorial, spring issue.',
        },
      ];
    case 'files':
      return [
        {
          type: 'document',
          label: 'Resume.pdf',
          value: 'https://example.com/resume.pdf',
          description: 'PDF · 184 KB',
        },
        {
          type: 'document',
          label: 'One pager.pdf',
          value: 'https://example.com/onepager.pdf',
          description: 'PDF · 92 KB',
        },
      ];
    case 'faq':
      return [
        {
          type: 'other',
          label: 'How fast can we start?',
          value: '',
          description:
            'Usually within a week of an intro call. Sometimes faster if the scope is tight.',
        },
        {
          type: 'other',
          label: 'Do you take on smaller projects?',
          value: '',
          description: 'Yes — anything where the team is small and the problem is interesting.',
        },
      ];
    case 'links':
    default:
      return [
        { type: 'website', label: 'Website', value: 'https://example.com' },
        { type: 'calendar', label: 'Book a call', value: 'https://cal.com/example' },
      ];
  }
}
